import { useState } from 'react';
import { assertNever } from '../../../lib/assertNever';

/** A row's id, as the list's API gives it. */
export type RowId = string | number;

/**
 * What is selected (design-system §6 *Selection*). Two modes, because "all N" is chosen before the
 * client has read every page (F5-h FR-U3), and unticking one row after `Select all` must keep the
 * other N − 1 rather than drop back to the page (F5-h WI-1.3 plan Decision 3):
 *
 * - `ids` — these rows, in the order they were selected (a `Set` keeps insertion order);
 * - `all` — every row of the list, less `excluded`. Its count follows the list's current total.
 */
export type RowSelectionState<Id extends RowId> =
    | { readonly kind: 'ids'; readonly ids: ReadonlySet<Id> }
    | { readonly kind: 'all'; readonly excluded: ReadonlySet<Id> };

/** The noun the bar counts in, sentence case: `{ one: 'account', other: 'accounts' }`. */
export interface SelectionNoun {
    readonly one: string;
    readonly other: string;
}

export interface UseRowSelectionOptions<Id extends RowId> {
    /** The ids of the rows on screen, in row order. */
    readonly pageIds: readonly Id[];
    /** The list's exact total — FS-10's `page.totalItems`. */
    readonly totalItems: number;
    readonly noun: SelectionNoun;
}

export interface RowSelection<Id extends RowId> {
    readonly state: RowSelectionState<Id>;
    /** `ids.size`, or `max(0, totalItems − excluded.size)`. */
    readonly count: number;
    readonly isSelected: (id: Id) => boolean;
    /** The header checkbox's state over `pageIds`; `'none'` when the page is empty. */
    readonly page: 'none' | 'some' | 'all';
    /** Whether the bar offers `Select all …`: the whole page is selected and the list has more. */
    readonly canSelectAll: boolean;
    /** The bar's visible count text. */
    readonly summary: string;
    /** `Select all <total> <plural noun>`. */
    readonly selectAllLabel: string;
    /** The last change, for `SelectionAnnouncer`; `''` until the first change. */
    readonly announcement: string;
    readonly toggleRow: (id: Id) => void;
    readonly togglePage: () => void;
    readonly selectAll: () => void;
    readonly clear: () => void;
}

const CLEARED = 'Selection cleared';

interface Derived {
    readonly count: number;
    readonly page: 'none' | 'some' | 'all';
    readonly canSelectAll: boolean;
    readonly summary: string;
}

function selectedIn<Id extends RowId>(state: RowSelectionState<Id>, id: Id): boolean {
    switch (state.kind) {
        case 'ids':
            return state.ids.has(id);
        case 'all':
            return !state.excluded.has(id);
        default:
            return assertNever(state);
    }
}

function countOf<Id extends RowId>(state: RowSelectionState<Id>, totalItems: number): number {
    switch (state.kind) {
        case 'ids':
            return state.ids.size;
        case 'all':
            return Math.max(0, totalItems - state.excluded.size);
        default:
            return assertNever(state);
    }
}

function nounFor(noun: SelectionNoun, count: number): string {
    return count === 1 ? noun.one : noun.other;
}

/** Everything the bar and the header read, from the state and the page on screen. */
function derive<Id extends RowId>(
    state: RowSelectionState<Id>,
    pageIds: readonly Id[],
    totalItems: number,
    noun: SelectionNoun,
): Derived {
    const count = countOf(state, totalItems);
    const onPage = pageIds.filter((id) => selectedIn(state, id)).length;
    const page = pageIds.length === 0 || onPage === 0 ? 'none' : onPage === pageIds.length ? 'all' : 'some';
    const canSelectAll = state.kind === 'ids' && page === 'all' && totalItems > count;
    return { count, page, canSelectAll, summary: summaryOf(state, count, pageIds, canSelectAll, noun) };
}

/**
 * The bar's count text (§6's own words for the page, sentence case per §9). `Selected <n> on this
 * page` only when every selected row is on screen, so a selection spanning pages never claims its
 * whole count is here.
 */
function summaryOf<Id extends RowId>(
    state: RowSelectionState<Id>,
    count: number,
    pageIds: readonly Id[],
    canSelectAll: boolean,
    noun: SelectionNoun,
): string {
    switch (state.kind) {
        case 'ids':
            return canSelectAll && state.ids.size === pageIds.length
                ? `Selected ${count} on this page`
                : `${count} ${nounFor(noun, count)} selected`;
        case 'all':
            return state.excluded.size === 0
                ? `All ${count} ${noun.other} selected`
                : `${count} ${nounFor(noun, count)} selected`;
        default:
            return assertNever(state);
    }
}

function isEmptyIds<Id extends RowId>(state: RowSelectionState<Id>): boolean {
    return state.kind === 'ids' && state.ids.size === 0;
}

interface Store<Id extends RowId> {
    readonly state: RowSelectionState<Id>;
    readonly announcement: string;
}

const EMPTY: RowSelectionState<never> = { kind: 'ids', ids: new Set<never>() };

/**
 * The selection state for one list (design-system §6 *Selection*; F5-h FR-U3): rows one at a time,
 * the current page by the header checkbox, and "all N" of the list as a separate action. Pure React
 * state with no API — the list's reading and acting are the consumer's (F5-h WI-1.3 plan Decision 2).
 *
 * The selection survives a change of `pageIds` or `totalItems` (paging, sorting); a consumer calls
 * `clear()` when a filter changes and after an action completes, which announces `Selection cleared`
 * only when something was selected.
 */
export function useRowSelection<Id extends RowId>({
    pageIds,
    totalItems,
    noun,
}: UseRowSelectionOptions<Id>): RowSelection<Id> {
    const [store, setStore] = useState<Store<Id>>({ state: EMPTY, announcement: '' });

    /** Moves to `next` and announces it: its summary, or the clearing when it emptied a selection. */
    function update(change: (state: RowSelectionState<Id>) => RowSelectionState<Id>) {
        setStore((previous) => {
            const next = change(previous.state);
            if (next === previous.state) {
                return previous;
            }
            if (isEmptyIds(next)) {
                return isEmptyIds(previous.state) ? previous : { state: next, announcement: CLEARED };
            }
            return { state: next, announcement: derive(next, pageIds, totalItems, noun).summary };
        });
    }

    function toggleRow(id: Id) {
        update((state) => {
            switch (state.kind) {
                case 'ids': {
                    const ids = new Set(state.ids);
                    if (!ids.delete(id)) {
                        ids.add(id);
                    }
                    return { kind: 'ids', ids };
                }
                case 'all': {
                    const excluded = new Set(state.excluded);
                    if (!excluded.delete(id)) {
                        excluded.add(id);
                    }
                    return excluded.size >= totalItems ? EMPTY : { kind: 'all', excluded };
                }
                default:
                    return assertNever(state);
            }
        });
    }

    function togglePage() {
        update((state) => {
            if (pageIds.length === 0) {
                return state;
            }
            const whole = pageIds.every((id) => selectedIn(state, id));
            switch (state.kind) {
                case 'ids': {
                    const ids = new Set(state.ids);
                    for (const id of pageIds) {
                        if (whole) {
                            ids.delete(id);
                        } else {
                            ids.add(id);
                        }
                    }
                    return { kind: 'ids', ids };
                }
                case 'all': {
                    if (whole) {
                        return EMPTY;
                    }
                    const excluded = new Set(state.excluded);
                    for (const id of pageIds) {
                        excluded.delete(id);
                    }
                    return { kind: 'all', excluded };
                }
                default:
                    return assertNever(state);
            }
        });
    }

    function selectAll() {
        update(() => ({ kind: 'all', excluded: new Set<Id>() }));
    }

    function clear() {
        update(() => EMPTY);
    }

    const derived = derive(store.state, pageIds, totalItems, noun);
    return {
        state: store.state,
        count: derived.count,
        isSelected: (id: Id) => selectedIn(store.state, id),
        page: derived.page,
        canSelectAll: derived.canSelectAll,
        summary: derived.summary,
        selectAllLabel: `Select all ${totalItems} ${noun.other}`,
        announcement: store.announcement,
        toggleRow,
        togglePage,
        selectAll,
        clear,
    };
}

/**
 * The ids to act on — the one place the mode becomes ids (F5-h WI-1.3 plan Decision 4). For `ids`,
 * the selected ids in the order they were selected (`allIds` is not read); for `all`, every id the
 * consumer read less the excluded ones, duplicates removed (a page that shifts while others change
 * the records can repeat one — F5-h FR-U3), first-seen order kept.
 */
export function resolveSelection<Id extends RowId>(
    state: RowSelectionState<Id>,
    allIds: readonly Id[],
): Id[] {
    switch (state.kind) {
        case 'ids':
            return [...state.ids];
        case 'all':
            return [...new Set(allIds)].filter((id) => !state.excluded.has(id));
        default:
            return assertNever(state);
    }
}
